import { OmsOrderDetail, OmsOrderPayment } from 'types/oms';
import LOCALS from 'commons/locals';
import { Trans, useTranslation } from 'react-i18next';
import Table, { ColumnsType } from 'antd/es/table';
import { CRYPTO_PAY_TYPE_LIST, OFFLINE_PAY_TYPE_LIST } from 'commons';
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  ORDER_PAYMENT_STATUS_MAP,
  getOrderPaymentStatusLabel,
  getOrderPaymentStatusTagColor,
} from 'constant/oms-order-payment-status';
import DualTimezoneTime from 'components/dual-timezone-time';
import {
  Button,
  Card,
  InputNumber,
  Modal,
  Select,
  Tag,
  Space,
  message,
} from 'antd';
import { financialManagementAccountList } from 'apis/fms';
import { FmsAccountManagement } from 'types/fms';
import { CreditCard } from 'lucide-react';
import commonApi from 'apis/common';
import JSONViewer from 'components/simple-json-viewer';
import {
  getOmsOrderDetailV2,
  updateOrderPayAmount,
  updateOrderPayType,
} from 'apis/oms';
import { UnwrapPromise } from 'types/base';
import { OrderPayReceiptImagesCell } from 'components/order-pay-receipt-images';
import { getStoreIdsByStockPlace } from 'utils/store-id-and-stock-place';
import { FMS_ACCOUNT_STATUS_MAP } from 'pages/fms/account-list';
import { useAppSelector } from 'store/hooks';
import { selectUserInfo } from 'store/slices/userInfoSlice';
import { getCurrencyDecimals, roundMoneyByCurrency } from 'utils/tools';

type OmsOrderDetailV2 = UnwrapPromise<ReturnType<typeof getOmsOrderDetailV2>>;

type OnlinePaymentRecordProps = {
  omsOrderDetail: OmsOrderDetail;
  stockPlace?: string;
  orderPayList: OmsOrderDetailV2['orderPayList'];
};

const getAccountName = (
  payType: string,
  preferredAccounts: FmsAccountManagement[],
  fallbackAccounts: FmsAccountManagement[] = [],
) => {
  return (
    preferredAccounts.find((item) => item.accountCode === payType)
      ?.accountName ||
    fallbackAccounts.find((item) => item.accountCode === payType)
      ?.accountName ||
    payType
  );
};

const PayTypeCell = ({
  record,
  orderId,
  allAccounts,
  shopAccounts,
  enabledOptions,
}: {
  record: OmsOrderPayment;
  orderId: number;
  allAccounts: FmsAccountManagement[];
  shopAccounts: FmsAccountManagement[];
  enabledOptions: { label: string; value: string }[];
}) => {
  const { i18n } = useTranslation();
  const { isAdmin } = useAppSelector(selectUserInfo);
  const [updating, setUpdating] = useState(false);
  const displayName = getAccountName(record.payType, shopAccounts, allAccounts);
  const canEdit =
    isAdmin &&
    shopAccounts.some((item) => item.accountCode === record.payType) &&
    record.payStatus === ORDER_PAYMENT_STATUS_MAP.PAID;

  if (!canEdit) {
    return <>{displayName}</>;
  }

  const options = enabledOptions.some((item) => item.value === record.payType)
    ? enabledOptions
    : [{ label: displayName, value: record.payType }, ...enabledOptions];

  return (
    <Select
      size="small"
      className="min-w-[160px]"
      value={record.payType}
      options={options}
      loading={updating}
      disabled={updating}
      popupMatchSelectWidth={false}
      placeholder={i18n.t(LOCALS.please_select) || ''}
      onChange={(nextPayType) => {
        if (nextPayType === record.payType) return;

        Modal.confirm({
          title: i18n.t(LOCALS.confirm_submit),
          content: i18n.t(LOCALS.confirm_adjust_payment_channel, {
            from: displayName,
            to: getAccountName(nextPayType, shopAccounts, allAccounts),
          }),
          onOk: async () => {
            setUpdating(true);
            try {
              await updateOrderPayType({
                orderId,
                payId: String(record.id),
                payType: nextPayType,
              });
              message.success(i18n.t(LOCALS.successful_operation));
              setTimeout(() => {
                window.location.reload();
              }, 500);
            } finally {
              setUpdating(false);
            }
          },
        });
      }}
    />
  );
};

const formatPayAmount = (amount: number) => amount.toLocaleString();

const PayAmountCell = ({
  record,
  orderId,
  shopAccounts,
}: {
  record: OmsOrderPayment;
  orderId: number;
  shopAccounts: FmsAccountManagement[];
}) => {
  const { i18n } = useTranslation();
  const { isAdmin } = useAppSelector(selectUserInfo);
  const currency = record.payCurrency || 'JPY';
  const [value, setValue] = useState(record.payAmount);
  const [updating, setUpdating] = useState(false);
  const confirmOpenRef = useRef(false);
  const canEdit =
    isAdmin &&
    shopAccounts.some((item) => item.accountCode === record.payType) &&
    record.payStatus === ORDER_PAYMENT_STATUS_MAP.PAID;

  if (!canEdit) {
    return <>{`${currency} ${formatPayAmount(record.payAmount)}`}</>;
  }

  const commit = (nextAmount: number | null) => {
    if (confirmOpenRef.current || updating) return;
    const next = roundMoneyByCurrency(Number(nextAmount), currency);
    if (!Number.isFinite(next) || next <= 0) {
      setValue(record.payAmount);
      return;
    }
    if (next === record.payAmount) {
      setValue(record.payAmount);
      return;
    }

    confirmOpenRef.current = true;
    Modal.confirm({
      title: i18n.t(LOCALS.confirm_submit),
      content: i18n.t(LOCALS.confirm_adjust_payment_amount, {
        currency,
        from: formatPayAmount(record.payAmount),
        to: formatPayAmount(next),
      }),
      onOk: async () => {
        setUpdating(true);
        try {
          await updateOrderPayAmount({
            orderId,
            payId: String(record.id),
            payAmount: next,
          });
          message.success(i18n.t(LOCALS.successful_operation));
          setTimeout(() => {
            window.location.reload();
          }, 500);
        } finally {
          setUpdating(false);
        }
      },
      onCancel: () => {
        setValue(record.payAmount);
      },
      afterClose: () => {
        confirmOpenRef.current = false;
      },
    });
  };

  return (
    <InputNumber
      size="small"
      className="min-w-[180px]"
      min={0.01}
      precision={getCurrencyDecimals(currency)}
      value={value}
      disabled={updating}
      addonBefore={currency}
      formatter={(amount) => `${amount}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
      parser={(amount) => amount!.replace(/,/g, '') as any}
      onChange={(next) => setValue(next ?? record.payAmount)}
      onPressEnter={() => commit(value)}
      onBlur={() => commit(value)}
    />
  );
};

const OnlinePaymentRecord = ({
  omsOrderDetail: { payList, id: orderId },
  orderPayList,
  stockPlace,
}: OnlinePaymentRecordProps) => {
  const { i18n } = useTranslation();
  const [paymentAccountList, setPaymentAccountList] = useState<
    FmsAccountManagement[]
  >([]);
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [detailModalData, setDetailModalData] = useState<any>();
  const storeId = getStoreIdsByStockPlace(stockPlace);

  useEffect(() => {
    financialManagementAccountList().then((res) => {
      setPaymentAccountList(res);
    });
  }, []);

  const shopAccountList = useMemo(() => {
    if (!storeId) return [];
    return paymentAccountList.filter((item) => item.storeId === storeId);
  }, [storeId, paymentAccountList]);
  const enabledPaymentOptions = useMemo(
    () =>
      shopAccountList
        .filter((item) => item.status === FMS_ACCOUNT_STATUS_MAP.ON)
        .map((item) => ({
          label: item.accountName,
          value: item.accountCode,
        })),
    [shopAccountList],
  );

  const columns: ColumnsType<OmsOrderPayment> = useMemo(() => {
    return [
      {
        title: <Trans i18nKey={LOCALS.payment_channel}></Trans>,
        dataIndex: 'payType',
        key: 'payType',
        render: (_: OmsOrderPayment['payType'], record: OmsOrderPayment) => {
          return (
            <PayTypeCell
              record={record}
              orderId={orderId}
              allAccounts={paymentAccountList}
              shopAccounts={shopAccountList}
              enabledOptions={enabledPaymentOptions}
            />
          );
        },
      },
      {
        title: <Trans i18nKey={LOCALS.pay_amount}></Trans>,
        dataIndex: 'payAmount',
        key: 'payAmount',
        render: (_: OmsOrderPayment['payAmount'], row: OmsOrderPayment) => {
          return (
            <PayAmountCell
              record={row}
              orderId={orderId}
              shopAccounts={shopAccountList}
            />
          );
        },
      },
      {
        title: <Trans i18nKey={LOCALS.payment_status}></Trans>,
        dataIndex: 'payStatus',
        key: 'payStatus',
        render: (payStatus: OmsOrderPayment['payStatus']) => {
          const text = payStatus ? getOrderPaymentStatusLabel(payStatus) : '-';

          return (
            <Tag color={getOrderPaymentStatusTagColor(payStatus)}>{text}</Tag>
          );
        },
      },
      {
        title: <Trans i18nKey={LOCALS.created_time}></Trans>,
        dataIndex: 'createTime',
        key: 'createTime',
        render: (createTime: OmsOrderPayment['createTime']) => {
          return <DualTimezoneTime time={createTime} stockPlace={stockPlace} />;
        },
      },
      {
        title: <Trans i18nKey={LOCALS.payment_time}></Trans>,
        dataIndex: 'payTime',
        key: 'payTime',
        render: (payTime: OmsOrderPayment['payTime']) => {
          return <DualTimezoneTime time={payTime} stockPlace={stockPlace} />;
        },
      },
      {
        title: <Trans i18nKey={LOCALS.payment_receipt_images}></Trans>,
        key: 'receiptImages',
        render: (row: OmsOrderPayment) => {
          const receiptKeys = orderPayList?.find(
            (i) => i.id === row.id,
          )?.receiptKeys;

          return (
            <OrderPayReceiptImagesCell
              orderId={orderId}
              payId={row.id}
              receiptKeys={receiptKeys || []}
            />
          );
        },
      },
      {
        title: <Trans i18nKey={LOCALS.option}></Trans>,
        key: 'operation',
        fixed: 'right',
        render: (record: OmsOrderPayment) => {
          const { payType, tradeNo, payStatus } = record;
          if (payType === 'NGENIUS_DUBAI_WEBSITE' && tradeNo) {
            return (
              <Space>
                <Button
                  type="link"
                  onClick={() => {
                    commonApi
                      .getNgeniusPaymentTransaction(tradeNo)
                      .then((res) => {
                        setDetailModalData(res);
                        setDetailModalOpen(true);
                      })
                      .catch(() => {
                        // 错误已在拦截器中处理
                      });
                  }}
                >
                  <Trans i18nKey={LOCALS.view}></Trans>
                </Button>
                {payStatus === ORDER_PAYMENT_STATUS_MAP.PENDING_PAYMENT && (
                  <Button
                    type="link"
                    onClick={() => {
                      Modal.confirm({
                        title: i18n.t(LOCALS.confirm_submit),
                        content: i18n.t('确认手动确认此笔支付？'),
                        onOk: async () => {
                          try {
                            await commonApi.manualConfirmNgeniusPayment(
                              tradeNo,
                            );
                            message.success(
                              i18n.t(LOCALS.successful_operation),
                            );
                            setTimeout(() => {
                              window.location.reload();
                            }, 500);
                          } catch (error) {
                            // 错误已在拦截器中处理
                          }
                        },
                      });
                    }}
                  >
                    手动确认
                  </Button>
                )}
              </Space>
            );
          }
          return '-';
        },
      },
    ];
  }, [
    enabledPaymentOptions,
    i18n,
    orderId,
    orderPayList,
    paymentAccountList,
    shopAccountList,
    stockPlace,
  ]);

  const onlinePayList = payList
    ? payList.filter(({ payType }) => {
        return ![...OFFLINE_PAY_TYPE_LIST, ...CRYPTO_PAY_TYPE_LIST].includes(
          payType,
        );
      })
    : [];

  if (!onlinePayList.length) {
    return null;
  }

  return (
    <Card bodyStyle={{ padding: '16px' }}>
      <div className="flex items-center gap-2 mb-4 text-base font-medium">
        <CreditCard className="w-5 h-5" />
        <Trans i18nKey={LOCALS.online_payment_record} />
      </div>
      <Table
        bordered
        size="small"
        rowKey={'id'}
        tableLayout="auto"
        scroll={{ x: 'max-content' }}
        pagination={false}
        columns={columns}
        dataSource={onlinePayList}
      />
      <Modal
        open={detailModalOpen}
        onCancel={() => setDetailModalOpen(false)}
        width="800px"
        footer={null}
      >
        {detailModalData && <JSONViewer data={detailModalData} />}
      </Modal>
    </Card>
  );
};

export default OnlinePaymentRecord;
